export type Ga4EcommerceEventName =
  | 'view_item'
  | 'add_to_cart'
  | 'begin_checkout'
  | 'payment_redirect'
  | 'purchase';

/** Fixed business roles required by the ARS GA4 spec. */
export const GA4_ITEM_CATEGORY = {
  MAIN: 'メイン商品',
  OPTION: 'オプション商品'
} as const;

export type Ga4ItemCategory =
  (typeof GA4_ITEM_CATEGORY)[keyof typeof GA4_ITEM_CATEGORY];

export interface Ga4Item {
  /** The product's own stable id. Options use their own, never the parent's. */
  item_id: string;
  item_name: string;
  /** ARS agent id of the direct-sales site. */
  affiliation: string;
  item_category: Ga4ItemCategory;
  /** `{plan}｜{unit}`. Omitted before plan and unit are known. */
  item_variant?: string;
  /** Discounted unit price, never price x quantity. */
  price?: number;
  /** Per-unit discount. Omitted when there is none. */
  discount?: number;
  quantity?: number;
  /** Parent product's item_id. Options only. */
  parent_item_id?: string;
  /** YYYY-MM-DD. Omitted for open-date products. */
  visit_date?: string;
}

export interface Ga4Ecommerce {
  currency?: string;
  value?: number;
  coupon?: string;
  /** purchase only. */
  transaction_id?: string;
  items: Ga4Item[];
}

export interface Ga4DataLayerEvent {
  event: Ga4EcommerceEventName;
  ui_language: string;
  /**
   * ARS agent id of the direct-sales site, from its site schema. Until the
   * schema lands, the one the API echoes on the product, session or booking.
   */
  agent_id: string;
  /** payment_redirect only. */
  payment_provider?: 'linkpay';
  ecommerce: Ga4Ecommerce;
}

export type Ga4AccountEventName =
  | 'view_login'
  | 'sign_up'
  | 'view_email_verification'
  | 'email_verification_complete'
  | 'login'
  | 'view_user_info'
  | 'profile_complete';

export type Ga4FlowContext = 'checkout' | 'standalone';

export const GA4_ENTRY_POINTS = [
  'checkout_gate',
  'header',
  'direct',
  'other'
] as const;

export type Ga4EntryPoint = (typeof GA4_ENTRY_POINTS)[number];

export const GA4_AUTH_METHODS = ['email', 'google', 'apple'] as const;

export type Ga4AuthMethod = (typeof GA4_AUTH_METHODS)[number];

export type Ga4AccountAction = 'created' | 'existing';

export type Ga4YesNo = 'yes' | 'no';

/** Auth params. Each event sends only the ones the spec lists for it. */
export interface Ga4AccountAuthParams {
  method?: Ga4AuthMethod;
  account_action?: Ga4AccountAction;
  email_verification_required?: Ga4YesNo;
}

export interface Ga4AccountDataLayerEvent extends Ga4AccountAuthParams {
  event: Ga4AccountEventName;
  ui_language: string;
  agent_id: string;
  flow_context: Ga4FlowContext;
  entry_point: Ga4EntryPoint;
}
